import React, { FC, useCallback } from 'react';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { Snackbar, Alert, Link, makeStyles, Button } from '@dxb-web-ui-framework/design-system';
import { Close as CloseIcon } from '@dxb-web-ui-framework/design-system/dist/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import {
  useNotificationsAuthorizationsList,
  useNotificationsTransactionsList,
  useNotificationsPayrollList,
  useNotificationsState,
  useNotificationsLoadingState,
} from '../../../../../authorizations/src';
import { getRoutePath } from '../../Routes/routePaths';

const useStyles = makeStyles(theme => ({
  snackbar: {
    width: 'auto',
    minWidth: '338px',
    backgroundColor: '#F49631',
  },
  anchorOriginTopCenter: {
    top: '20px',
    position: 'absolute',
  },
  filledWarning: {
    fontWeight: 500,
    color: '#FFF',
    '& :last-child': {
      color: '#FFF',
    },
  },
  button: {
    backgroundColor: '#F49631',
    '&:hover': {
      backgroundColor: '#F2B16D',
    },
    '& svg': {
      fill: '#FFF',
    },
  },
}));

const TransactionsNotification: FC = (): Nullable<JSX.Element> => {
  const classes = useStyles();
  const navigate = useNavigate();
  const selectedCustomerId = useSelectedCustomerId();
  const authorizationsList = useNotificationsAuthorizationsList();
  const transactionsList = useNotificationsTransactionsList();
  const payrollList = useNotificationsPayrollList();
  const pendingAuthorizationsLoading = useNotificationsLoadingState('isLoadingPendingAuthorizations');
  const { isNotificationsSnackbarOpen, onToggleNotificationsSnackbar } = useNotificationsState();
  const authorizationsListIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.PendingAuthorizationsList);

  const { t } = useTranslation();

  const pendingTransactionsCount =
    (authorizationsList?.length || 0) + (transactionsList?.length || 0) + (payrollList?.length || 0);

  const handleViewLinkClick = useCallback(() => {
    selectedCustomerId &&
      navigate(
        getRoutePath({
          routeKey: 'authorizations',
          customerId: selectedCustomerId,
        }),
      );
  }, [navigate, selectedCustomerId]);

  const handleCloseBtnClick = useCallback(
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      onToggleNotificationsSnackbar();
    },
    [onToggleNotificationsSnackbar],
  );

  return authorizationsListIsAllowed && pendingTransactionsCount > 0 && !pendingAuthorizationsLoading ? (
    <Snackbar
      open={isNotificationsSnackbarOpen}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      className={classes.snackbar}
      classes={{ anchorOriginTopCenter: classes.anchorOriginTopCenter }}
      autoHideDuration={null}>
      <>
        <Alert
          action={
            <Link size='small' textColor='textSecondary' onClick={handleViewLinkClick}>
              {t('App.Header.Snackbar.Alert.View')}
            </Link>
          }
          classes={{ filledWarning: classes.filledWarning }}
          severity='warning'
          variant='filled'
          icon={false}>
          {pendingTransactionsCount === 1
            ? `${t('App.Header.Snackbar.Alert.PendingTransactionsCount.One')}`
            : `${t('App.Header.Snackbar.Alert.PendingTransactionsCount.Many', {
                number: pendingTransactionsCount,
              })}`}
        </Alert>
        <Button
          classes={{ root: classes.button }}
          aria-label={t('transactionsNotification.CloseBtn.AriaLabel')}
          color='primary'
          isIconButton
          size='large'
          title={t('transactionsNotification.CloseBtn.Title')}
          variant='text'
          onClick={handleCloseBtnClick}>
          <CloseIcon />
        </Button>
      </>
    </Snackbar>
  ) : null;
};

export { TransactionsNotification };
